import React, { useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  useCreateCompanyRoleMutation,
  useDestroyCompanyRoleMutation,
  useUpdateCompanyRoleMutation,
} from 'components/hooks/companyRoleMutations';
import { useCompanyRolesQuery } from 'components/hooks/companyRoleQueries';
import { useCompanyRolesCourseCompanyRolesQuery } from 'components/hooks/companyRolesCourseCompanyRoleQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';
import useValidatedStore from 'components/hooks/useValidatedStore';
import useWindowStorage from 'components/hooks/useWindowStorage';

import DestroyModal from 'components/application/DestroyModal';
import Paginator from 'components/application/Paginator';
import ResourceBlankNotice from 'components/application/ResourceBlankNotice';
import SuccessToast from 'components/application/SuccessToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import RolesBar from 'components/roles/RolesBar';
import RolesSidePanel from 'components/roles/RolesSidePanel';
import RolesTable from 'components/roles/RolesTable';

import {
  paramsForCreate,
  paramsForUpdate,
} from 'components/helpers/resources/courseCompanyRoles';

const defaultRole = {
  courseCompanyRoles: [],
  position: '',
};

const initialTabStore = {
  currentResourceId: '',
  currentSearch: '',
  isSidePanelOpen: false,
  page: 1,
};

const tabSchema = z.object({
  currentResourceId: z.string().nullable(),
  currentSearch: z.string(),
  isSidePanelOpen: z.boolean(),
  page: z.number().or(z.null()),
});

export default function RolesTab() {
  const { addInedibleToast, addToast } = useBreadBoard();
  const currentActor = useCurrentActor();

  const { hasRoleEditableAccess, isMultiDivisionAccount } =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'roles.RolesTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [getStore, setStore] = useWindowStorage('trainingRegister|roles', {
    store: window.sessionStorage,
  });

  const tabStore = useValidatedStore({
    getStore,
    initialStore: initialTabStore,
    schema: tabSchema,
  });

  const initialCurrentSearch = { rolesSearch: tabStore.currentSearch };

  const [sidePanelContext, setSidePanelContext] = useState('show');

  const [selectedRoleId, setSelectedRoleId] = useState(
    tabStore.currentResourceId ?? null,
  );

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(
    tabStore.isSidePanelOpen && !!tabStore.currentResourceId,
  );

  const [currentRole, setCurrentRole, handleRoleInputChange] =
    useForm(defaultRole);

  const [
    requestError,
    isSubmitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [currentPage, setCurrentPage] = useState(tabStore.page || 1);

  const [currentSearch, setCurrentSearch, handleSearchInputChange] =
    useForm(initialCurrentSearch);

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: ({ currentValue, prevValue }) => {
      if (currentValue.rolesSearch !== prevValue.rolesSearch) {
        setCurrentPage(1);
      }
    },
  });

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  const {
    data: domainCompanyRoles,
    isFetching: isDomainCompanyRolesFetching,
    isLoading: isDomainCompanyRolesLoading,
    isSuccess: isDomainCompanyRolesSuccess,
  } = useCompanyRolesQuery({
    params: {
      page: currentPage,
      search: debouncedCurrentSearch.rolesSearch,
    },
    queryOptions: { keepPreviousData: true },
  });

  const selectedRole = domainCompanyRoles?.data.find(
    (companyRole) => companyRole.id === selectedRoleId,
  );

  const isTrainingRegisterEnabled =
    currentActor.isAllowedFeature('training_register');

  const {
    data: domainCourseCompanyRoles,
    isSuccess: isDomainCourseCompanyRolesFetchSuccess,
  } = useCompanyRolesCourseCompanyRolesQuery({
    companyRoleId: selectedRoleId,
    queryOptions: {
      enabled:
        isTrainingRegisterEnabled &&
        isSidePanelOpen &&
        sidePanelContext !== 'new' &&
        !!selectedRole,
    },
  });

  const createCompanyRoleMutation = useCreateCompanyRoleMutation();
  const updateCompanyRoleMutation = useUpdateCompanyRoleMutation();
  const destroyCompanyRoleMutation = useDestroyCompanyRoleMutation();

  const openSidePanel = () => {
    setIsSidePanelOpen(true);
  };

  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
  };

  const handleAddClick = (event) => {
    event.currentTarget.blur();
    openSidePanel();
    resetRequestError();
    setCurrentRole(defaultRole);
    setSelectedRoleId(null);
    setSidePanelContext('new');
  };

  const handleCreateClick = () => {
    createCompanyRoleMutation.mutate(
      {
        params: {
          position: currentRole.position,
          course_company_roles_attributes: paramsForCreate({
            currentId: selectedRoleId,
            currentType: 'company_role',
            frontendCourseCompanyRoles: currentRole.courseCompanyRoles,
          }),
        },
      },
      {
        onSuccess: (data) => {
          addSuccessToast({
            i18nKey: 'shared.success.added',
            name: data.data.attributes.position,
          });

          resetRoles();
        },
        onError: handleRequestError,
      },
    );
  };

  const handleUpdateClick = () => {
    updateCompanyRoleMutation.mutate(
      {
        id: selectedRoleId,
        params: {
          position: currentRole.position,
          course_company_roles_attributes: paramsForUpdate({
            currentId: selectedRoleId,
            currentType: 'company_role',
            frontendCourseCompanyRoles: currentRole.courseCompanyRoles,
            serverCourseCompanyRoles: domainCourseCompanyRoles?.data || [],
          }),
        },
      },
      {
        onSuccess: (data) => {
          addSuccessToast({
            i18nKey: 'shared.success.updated',
            name: data.data.attributes.position,
          });

          resetRoles();
          resetRequestError();
        },
        onError: handleRequestError,
      },
    );
  };

  const handleDestroyClick = () => {
    destroyCompanyRoleMutation.mutate(
      { id: selectedRoleId },
      {
        onSuccess: () => {
          addSuccessToast({
            i18nKey: 'shared.success.deleted',
            name: selectedRole.attributes.position,
          });

          resetRoles();
        },
        onError: () => addInedibleToast(),
      },
    );
  };

  const resetRoles = () => {
    closeSidePanel();
    setCurrentPage(1);
    setCurrentRole(defaultRole);
    setSelectedRoleId(null);
  };

  const addSuccessToast = ({ i18nKey, name }) => {
    addToast(
      <SuccessToast
        message={
          <Trans
            components={{ span: <span className='tw-font-medium' /> }}
            i18nKey={i18nKey}
            ns='common'
            tOptions={{ interpolation: { escapeValue: false } }}
            values={{ name }}
          />
        }
      />,
    );
  };

  const handleRoleClick = ({ roleId }) => {
    openSidePanel();
    resetRequestError();
    setSelectedRoleId(roleId);
    setSidePanelContext('show');
  };

  const handleEditClick = () => {
    setCurrentRole({
      courseCompanyRoles:
        domainCourseCompanyRoles?.data.map((courseCompanyRole) => ({
          courseId: courseCompanyRole.relationships.course.data.id,
          required: courseCompanyRole.attributes.required,
        })) || [],
      position: selectedRole.attributes.position,
    });

    setSidePanelContext('edit');
  };

  const handleRoleOptionChange = (courseId) => {
    const newCourseCompanyRoles = [...currentRole.courseCompanyRoles];
    newCourseCompanyRoles.push({ courseId, required: true });

    setCurrentRole({
      ...currentRole,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  const handleCourseCompanyRoleRequiredChange = (courseId, value) => {
    const newCourseCompanyRoles = [...currentRole.courseCompanyRoles];

    const courseCompanyRoleIndex = newCourseCompanyRoles.findIndex(
      (ccr) => ccr.courseId === courseId,
    );

    newCourseCompanyRoles[courseCompanyRoleIndex] = {
      ...newCourseCompanyRoles[courseCompanyRoleIndex],
      required: value,
    };

    setCurrentRole({
      ...currentRole,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  const handleCourseCompanyRoleDelete = (courseId) => {
    const newCourseCompanyRoles = currentRole.courseCompanyRoles.filter(
      (ccr) => ccr.courseId !== courseId,
    );

    setCurrentRole({
      ...currentRole,
      courseCompanyRoles: newCourseCompanyRoles,
    });
  };

  useEffect(() => {
    setStore({
      currentResourceId: selectedRoleId,
      currentSearch: currentSearch.rolesSearch,
      isSidePanelOpen,
      page: currentPage,
    });
  }, [currentPage, currentSearch.rolesSearch, isSidePanelOpen, selectedRoleId]);

  (function checkSelectedRole() {
    if (selectedRoleId && isDomainCompanyRolesSuccess && !selectedRole) {
      setIsSidePanelOpen(false);
      setSelectedRoleId(null);
    }
  })();

  const isLoading =
    isDomainCompanyRolesFetching ||
    !isDomainCompanyRolesSuccess ||
    currentSearch.rolesSearch !== debouncedCurrentSearch.rolesSearch;

  return (
    <>
      <div className='col-sm-12 tw-px-0'>
        {(isDomainCompanyRolesLoading ||
          domainCompanyRoles?.meta.totalCount > 0) && (
          <RolesBar
            isSearchDisabled={isDomainCompanyRolesLoading}
            onAddClick={handleAddClick}
            onSearchInputChange={handleSearchInputChange}
            onSearchReset={() => {
              setCurrentSearch({ ...currentSearch, rolesSearch: '' });
            }}
            rolesSearch={currentSearch.rolesSearch}
          />
        )}
        {isLoading || domainCompanyRoles.data.length > 0 ?
          <>
            <RolesTable
              domainRoles={domainCompanyRoles}
              isLoading={isLoading}
              onRoleClick={handleRoleClick}
            />
            {isDomainCompanyRolesSuccess &&
              domainCompanyRoles.meta.totalPages > 1 && (
                <div className='tw-mt-20 tw-text-center'>
                  <Paginator
                    currentPage={domainCompanyRoles.meta.currentPage}
                    onClick={(event) => {
                      setCurrentPage(
                        Number(event.target.getAttribute('data-page')),
                      );
                    }}
                    totalPages={domainCompanyRoles.meta.totalPages}
                  />
                </div>
              )}
          </>
        : isDomainCompanyRolesSuccess && (
            <ResourceBlankNotice
              addButtonText={i18nTCommon('roles.addNewRole')}
              isAddable={hasRoleEditableAccess}
              noResourcesDescription={i18nT(
                `blankNotice.noResourcesDescription.${hasRoleEditableAccess ? 'editable' : 'readOnly'}`,
              )}
              noResourcesFoundTitle={i18nTCommon('roles.noRolesFound')}
              noResourcesTitle={i18nT(
                `blankNotice.noResourcesTitle.${hasRoleEditableAccess ? 'editable' : 'readOnly'}`,
              )}
              onAdd={handleAddClick}
              totalCount={domainCompanyRoles.meta.totalCount}
            />
          )
        }
      </div>
      <RolesSidePanel
        closeSidePanel={closeSidePanel}
        currentRole={currentRole}
        domainCourseCompanyRoles={domainCourseCompanyRoles}
        isEditDisabled={
          isTrainingRegisterEnabled && !isDomainCourseCompanyRolesFetchSuccess
        }
        isOpen={
          // isSidePanelOpen can be restored from tabStore before the selected role has loaded
          isSidePanelOpen && (sidePanelContext === 'new' || !!selectedRole)
        }
        isSubmitDisabled={isSubmitDisabled}
        onCourseCompanyRoleDelete={handleCourseCompanyRoleDelete}
        onCourseCompanyRoleRequiredChange={
          handleCourseCompanyRoleRequiredChange
        }
        onCreateClick={handleCreateClick}
        onDeleteClick={() => setIsDestroyModalOpen(true)}
        onEditClick={handleEditClick}
        onRoleInputChange={handleRoleInputChange}
        onRoleOptionChange={handleRoleOptionChange}
        onUpdateClick={handleUpdateClick}
        removeErrorStyling={removeErrorStyling}
        requestError={requestError}
        selectedRole={selectedRole}
        sidePanelContext={sidePanelContext}
      />
      {sidePanelContext === 'show' && selectedRole && (
        <DestroyModal
          additionalContent={
            isMultiDivisionAccount && (
              <p className='tw-m-0 tw-mt-5 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-3 tw-text-red-800'>
                {i18nT('destroyModal.additionalContent')}
              </p>
            )
          }
          confirmationText={i18nTCommon('shared.deleteName', {
            interpolation: { escapeValue: false },
            name: selectedRole.attributes.position,
          })}
          displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
            interpolation: { escapeValue: false },
            name: selectedRole.attributes.position,
          })}
          isOpen={isDestroyModalOpen}
          onClose={() => setIsDestroyModalOpen(false)}
          onDestroy={handleDestroyClick}
        />
      )}
    </>
  );
}
